import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext(null);

// Helper para obtener la fecha actual en formato YYYY-MM-DD
const getTodayKey = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp debe usarse dentro de AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Estado del modo de aplicación (POS | FACTURACION)
  const [modo, setModo] = useState(null);
  
  // Estado de la caja (abierta | cerrada)
  const [cajaEstado, setCajaEstado] = useState('cerrada');
  
  // Estado del diálogo de selección inicial
  const [mostrarDialogoSeleccion, setMostrarDialogoSeleccion] = useState(false);

  // Estado de la empresa
  const [business, setBusiness] = useState({
    name: 'Mi Negocio',
    nit: '0000-000000-000-0',
    address: '',
    phone: '',
  });

  // Estado del turno actual
  const [currentShift, setCurrentShift] = useState(null);

  // Estado del cajón (dinero en caja)
  const [cashDrawer, setCashDrawer] = useState({
    initialAmount: 0,
    currentAmount: 0,
    addedAmount: 0,
    removedAmount: 0,
  });

  // Estado de notificaciones
  const [notifications, setNotifications] = useState([]);

  // Estado de configuración global
  const [settings, setSettings] = useState({
    currency: 'USD',
    currencySymbol: '$',
    decimalPlaces: 2,
    theme: 'light',
    receiptPrinter: null,
    invoicePrinter: null,
  });

  // Estado del modo oscuro (día/noche)
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('nx2026_darkMode');
    return stored ? JSON.parse(stored) : false;
  });

  // Función para togglear el modo oscuro
  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('nx2026_darkMode', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  // Cargar modo desde localStorage al iniciar
  useEffect(() => {
    const checkSelectionNeeded = () => {
      const todayKey = getTodayKey();
      const storedModo = localStorage.getItem(`nx2026_modo_${todayKey}`);
      
      // Obtener la indicación del servidor sobre si mostrar el diálogo
      const showSelectionFromLogin = localStorage.getItem('nx2026_showSelectionDialog');
      const showSelectionDialogParsed = showSelectionFromLogin ? JSON.parse(showSelectionFromLogin) : null;
      
      if (storedModo) {
        setModo(storedModo);
        setMostrarDialogoSeleccion(false);
      } else if (showSelectionDialogParsed === false) {
        // El servidor dijo que no mostrar el diálogo (usuario tiene un solo módulo)
        setMostrarDialogoSeleccion(false);
      } else {
        // Mostrar diálogo por defecto o si el servidor dijo true
        setMostrarDialogoSeleccion(true);
      }
    };

    checkSelectionNeeded();
    
    // Escuchar evento personalizado de login exitoso
    const handleLoginSuccess = () => {
      checkSelectionNeeded();
    };
    
    // Escuchar evento de modo seleccionado automáticamente
    const handleModoSeleccionado = (event) => {
      if (event.detail && event.detail.modo) {
        setModo(event.detail.modo);
        setMostrarDialogoSeleccion(false);
      }
    };
    
    window.addEventListener('nx2026-login-success', handleLoginSuccess);
    window.addEventListener('nx2026-modo-seleccionado', handleModoSeleccionado);
    
    return () => {
      window.removeEventListener('nx2026-login-success', handleLoginSuccess);
      window.removeEventListener('nx2026-modo-seleccionado', handleModoSeleccionado);
    };
  }, []);

  // Función para establecer el modo y persistir
  const handleSetModo = useCallback((newModo) => {
    setModo(newModo);
    const todayKey = getTodayKey();
    localStorage.setItem(`nx2026_modo_${todayKey}`, newModo);
    setMostrarDialogoSeleccion(false);
  }, []);

  // Función para cambiar el modo manualmente (desde sidebar)
  const toggleModo = useCallback(() => {
    setModo(currentModo => {
      const newModo = currentModo === 'POS' ? 'FACTURACION' : 'POS';
      // Persistir en localStorage
      const todayKey = getTodayKey();
      localStorage.setItem(`nx2026_modo_${todayKey}`, newModo);
      return newModo;
    });
    setMostrarDialogoSeleccion(false);
  }, []);

  // Función para forzar mostrar el diálogo de selección
  const reiniciarDialogoSeleccion = useCallback(() => {
    setMostrarDialogoSeleccion(true);
  }, []);

  // Función para agregar notificación
  const addNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    
    // Auto-eliminar después de 5 segundos
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  // Función para cerrar turno
  const closeShift = (finalAmount) => {
    setCurrentShift(null);
    setCajaEstado('cerrada');
    setCashDrawer({
      initialAmount: 0,
      currentAmount: finalAmount || 0,
      addedAmount: 0,
      removedAmount: 0,
    });
  };

  // Función para abrir turno
  const openShift = (initialAmount) => {
    setCurrentShift({
      id: Date.now(),
      startTime: new Date(),
      initialAmount,
    });
    setCajaEstado('abierta');
    setCashDrawer({
      initialAmount,
      currentAmount: initialAmount,
      addedAmount: 0,
      removedAmount: 0,
    });
  };

  const value = {
    // Modo de aplicación
    modo,
    setModo: handleSetModo,
    toggleModo,
    reiniciarDialogoSeleccion,
    
    // Estado de caja
    cajaEstado,
    setCajaEstado,
    
    // Diálogo de selección
    mostrarDialogoSeleccion,
    setMostrarDialogoSeleccion,
    
    // Empresa
    business,
    setBusiness,
    
    // Turno
    currentShift,
    setCurrentShift,
    
    // Cajón
    cashDrawer,
    setCashDrawer,
    
    // Notificaciones
    notifications,
    addNotification,
    
    // Configuración
    settings,
    setSettings,
    
    // Modo oscuro
    darkMode,
    toggleDarkMode,
    
    // Funciones
    openShift,
    closeShift,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
